import { Injectable } from '@angular/core';
import { AuthUser } from '@app/account/account-api.model';
import { FooterButtonStates } from '@app/onboarding/components/onboarding-footer/onboarding-footer.model';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { TrackerStep } from '../components/tracker/tracker.component';

export interface OnboardingConfig {
  steps: number;
  templatePrefix: string;
  jobRoleProviderName: string;
}

export interface OnboardingInfo {
  jobRoleProviderName: string;
}
@Injectable({
  providedIn: 'root',
})
export class OnboardingConfigService {
  public i18n = this.translateService.instant([
    'Onboarding_A11ySkillsStepName',
    'Onboarding_A11yStartLearningStepName',
    'Onboarding_A11yAssignedStepName',
    'Onboarding_A11yRoleSelectStepName',
    'Onboarding_UploadResumeHeader',
    'Onboarding_ReviewSkillsHeader',
  ]);
  private footerButtonSubject = new BehaviorSubject<FooterButtonStates>({
    currentStep: OnboardingStep.welcome,
    previousButton: '',
    nextButton: '',
  });
  private onboardingConfigInfoCache: Observable<OnboardingConfig>;

  constructor(
    private http: NgxHttpClient,
    private authService: AuthService,
    private translateService: TranslateService
  ) {}

  public get footerButtonAction(): Observable<FooterButtonStates> {
    return this.footerButtonSubject.asObservable();
  }

  /**
   * Get a cached observable of the cached onboarding config
   */
  public get config(): Observable<OnboardingConfig> {
    if (!this.onboardingConfigInfoCache) {
      this.onboardingConfigInfoCache = this.getOnboardingInfo().pipe(
        shareReplay(1)
      );
    }
    return this.onboardingConfigInfoCache;
  }

  public get organizationNameInOnboarding(): string {
    const authUser = this.authService.authUser;
    return authUser?.defaultOrgInfo?.organizationBranding
      ?.organizationNameInOnboarding
      ? this.authService.authUser.defaultOrgInfo.name
      : 'Degreed';
  }

  /**
   *  Set the next footer button state to the stream based on the page the user is on
   *  @param buttonStates: FooterButtonStates the current state of the footer buttons
   */
  public footerButtonStateChange(buttonStates: FooterButtonStates): void {
    this.footerButtonSubject.next(buttonStates);
  }

  /**
   * Update the active step to active, and set the rest as inactive
   * @param steps: tracker steps
   * @param activeStep: what is the active step
   */
  public updateActiveTrackerStep(
    steps: TrackerStep[],
    activeStep: OnboardingStep
  ): TrackerStep[] {
    steps.forEach((step) => {
      if (step.stepName !== activeStep) {
        step.isActive = false;
      } else {
        step.isActive = true;
      }
    });
    return steps;
  }
  /**
   * Get onboarding steps states for progress tracker
   * @param templatePrefix: string determines if hmm/Lxp/dsi
   * @param activeStep: string name of the current step that is active
   * @param skillsSelected: boolean used to determine if the skills steps should be disabled.
   */
  public getTrackerSteps(
    templatePrefix: string,
    activeStep: OnboardingStep = OnboardingStep.welcome,
    itemsSelected: boolean = false
  ): TrackerStep[] {
    switch (templatePrefix) {
      case 'hmm-':
        return [
          {
            stepDisplayName: this.i18n.Onboarding_A11ySkillsStepName,
            stepName: OnboardingStep.skills,
            stepUrl: '/getstarted/skills',
            isActive: activeStep === OnboardingStep.skills,
            isDisabled: false,
          },
          {
            stepDisplayName: this.i18n.Onboarding_A11yStartLearningStepName,
            stepName: OnboardingStep.startLearning,
            stepUrl: '/getstarted/startlearning',
            isActive: activeStep === OnboardingStep.startLearning,
            isDisabled: !itemsSelected,
          },
        ];
      default:
        // if the user chooses a role with assigned skills it does not update the tracker until "next/skip"
        const disableResumeParsing: boolean =
          this.authService.authUser?.disableResumeParsingInOnboarding;
        if (disableResumeParsing) {
          return [
            {
              stepDisplayName: this.i18n.Onboarding_A11yRoleSelectStepName,
              stepName: OnboardingStep.roleselect,
              stepUrl: '/getstarted/roleselect',
              isActive: activeStep === OnboardingStep.roleselect,
              isDisabled: false,
            },
            {
              stepDisplayName: this.i18n.Onboarding_A11yAssignedStepName,
              stepName: OnboardingStep.assigned,
              stepUrl: '/getstarted/assigned',
              isActive: activeStep === OnboardingStep.assigned,
              isDisabled: !itemsSelected,
            },
            {
              stepDisplayName: this.i18n.Onboarding_ReviewSkillsHeader,
              stepName: OnboardingStep.review,
              stepUrl: '/getstarted/review',
              isActive: activeStep === OnboardingStep.review,
              isDisabled: false,
            },
          ];
        } else {
          return [
            {
              stepDisplayName: this.i18n.Onboarding_A11yRoleSelectStepName,
              stepName: OnboardingStep.roleselect,
              stepUrl: '/getstarted/roleselect',
              isActive: activeStep === OnboardingStep.roleselect,
              isDisabled: false,
            },
            {
              stepDisplayName: this.i18n.Onboarding_A11yAssignedStepName,
              stepName: OnboardingStep.assigned,
              stepUrl: '/getstarted/assigned',
              isActive: activeStep === OnboardingStep.assigned,
              isDisabled: !itemsSelected,
            },
            {
              stepDisplayName: this.i18n.Onboarding_UploadResumeHeader,
              stepName: OnboardingStep.resume,
              stepUrl: '/getstarted/resume',
              isActive: activeStep === OnboardingStep.resume,
              isDisabled: false,
            },
            {
              stepDisplayName: this.i18n.Onboarding_ReviewSkillsHeader,
              stepName: OnboardingStep.review,
              stepUrl: '/getstarted/review',
              isActive: activeStep === OnboardingStep.review,
              isDisabled: false,
            },
          ];
        }
    }
  }

  /**
   * Determine the previous steps url.
   * @param currentStep - The current step the user is on steps include
   *  - welcome
   *  - roleselect
   *  - assigned
   *  - resume
   *  - skills
   *  - startLearning
   * @param templatePrefix - template from config
   *  - hmm-
   *  - dsa-
   *  - (blank) everything else(lxp)
   *
   */
  public getPreviousUrl(
    currentStep: OnboardingStep,
    trackerSteps?: TrackerStep[]
  ): string {
    switch (currentStep) {
      case 'welcome':
        return '';
      case 'roleselect':
        return '/getstarted/welcome';
      case 'assigned':
        return '/getstarted/roleselect';
      case 'resume':
        if (trackerSteps) {
          const i = trackerSteps.findIndex(
            (step) => step.stepName === currentStep
          );
          if (i > 0) {
            return '/getstarted/' + trackerSteps[i - 1].stepName;
          }
        }
        return '/getstarted/assigned';
      case 'skills':
        return '/getstarted/welcome';
      case 'review':
        const disableResumeParsing: boolean =
          this.authService.authUser?.disableResumeParsingInOnboarding;
        if (disableResumeParsing) {
          if (trackerSteps) {
            const i = trackerSteps.findIndex(
              (step) => step.stepName === currentStep
            );
            if (i > 0) {
              return '/getstarted/' + trackerSteps[i - 1].stepName;
            }
          }
          return '/getstarted/assigned';
        } else {
          return '/getstarted/resume';
        }
      case 'startLearning':
        // hmm as of right now will be the config that has the startlearning page
        return '/getstarted/skills';
    }
  }

  /**
   * Determine the previous steps url.
   * @param currentStep - The current step the user is on steps include
   *  - welcome
   *  - roleselect
   *  - assigned
   *  - resume
   *  - skills
   *  - startlearning
   * @param templatePrefix - template from config
   *  - hmm-
   *  - dsa-
   *  - (blank) everything else(lxp)
   *
   */
  public getNextUrl(
    currentStep: OnboardingStep,
    templatePrefix: string
  ): string {
    switch (currentStep) {
      case 'welcome':
        if (templatePrefix === 'hmm-') {
          return '/getstarted/skills';
        } else {
          return '/getstarted/roleselect';
        }
      case 'roleselect':
        return '/getstarted/assigned';
      case 'assigned':
        const disableResumeParsing: boolean =
          this.authService.authUser?.disableResumeParsingInOnboarding;
        if (disableResumeParsing) {
          return '/getstarted/review';
        } else {
          return '/getstarted/resume';
        }
      case 'resume':
        return '/getstarted/review';
      case 'skills':
        return '/getstarted/startlearning';
      case 'review':
        return `/profile/${this.authService.authUser.viewerProfile.profileUrl}/overview`;
      case 'startLearning':
        const vanityUrl = this.authService.authUser.viewerProfile.vanityUrl;
        return `/${vanityUrl}/dashboard/discover`;
    }
  }

  private getOnboardingInfo(): Observable<OnboardingConfig> {
    return this.http.get<any>(`/onboarding/getonboardinginfo`, {}).pipe(
      map((info: OnboardingInfo) => {
        const authuser: AuthUser = this.authService.authUser;
        const isClientProvider: boolean =
          authuser?.defaultOrgInfo?.settings.isClientProvider;
        const isSkillAnalyticsClient: boolean = authuser.isSkillAnalyticsClient;
        const isSkillInventoryClient: boolean = authuser.isSkillInventoryClient;
        let stepConfig;
        if (isClientProvider) {
          stepConfig = {
            templatePrefix: 'hmm-',
            jobRoleProviderName: info.jobRoleProviderName,
          };
        } else if (isSkillInventoryClient || isSkillAnalyticsClient) {
          stepConfig = {
            templatePrefix: 'dsa-',
            jobRoleProviderName: info.jobRoleProviderName,
          };
        } else {
          stepConfig = {
            templatePrefix: '',
            jobRoleProviderName: info.jobRoleProviderName,
          };
        }
        return stepConfig;
      }),
      catchError((error) => throwError(error))
    );
  }
}
